import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { find } from 'lodash'
import { Trans } from '@lingui/macro'
import classNames from 'classnames'
import ReactPlayer from 'react-player'
import useHover from 'react-use-hover'
import Slider from 'rc-slider'
import { Box } from 'front-end/src/components'
import IconBan from '../../../../../components/common/icon/IconBan'
import IconGhost from '../../../../../components/common/icon/IconGhost'

import './MediaElement.less'
import { IElement } from '../../../types/IElement'
import { IDocument } from 'shared/IDocument'
import { Dropdown } from 'front-end/src/components/common/dropdown/Dropdown'
import 'rc-slider/assets/index.css'
import { VideoEngagementOrigin } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  CreateMediaImpressionDocument,
  StoreMediaEngagementDocument,
  StoreMediaEngagementMutation,
  StoreMediaEngagementMutationVariables
} from './MediaElement.codegen'
import { apolloClientPrivate } from 'front-end/src/graphql/apolloClient'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'

const isChrome = window.navigator.vendor === 'Google Inc.'

interface IEngagementTracker {
  shouldFlush: boolean
  isPlaying: boolean
  watchingStartedAt: number // FROM time
  watchingEndedAt: number // TO time
  impressionId: number | null | undefined
}

const isChromeLike = /Chrome/.test(navigator.userAgent)

const AUTO_FLUSH_TIMEOUT_SEC = 10

export const MediaElement: FC<
  IElement & {
    isVisible?: boolean
    shouldTrackMedia: boolean
    topicId: number
    hidePlaybackSpeedDropdown?: boolean
  }
> = (props) => {
  const { isAdmin } = usePublicApiSession()
  // Video analytics tracking object
  const { featureFlags } = useFeatureFlags()

  const engagement = useRef<IEngagementTracker>({
    shouldFlush: false,
    isPlaying: false,
    watchingStartedAt: 0,
    watchingEndedAt: 0,
    impressionId: undefined
  })

  const createMediaImpression = async () => {
    if (!featureFlags?.video_analytics) {
      return
    }
    // Undefined impressionId means it was not fetched yet.
    if (engagement.current.impressionId !== undefined || !props.topicId) {
      return // No need to get impression more than once
    }

    const { data } = await apolloClientPrivate.mutate({
      mutation: CreateMediaImpressionDocument,
      variables: {
        topicId: props.topicId,
        elementId: props.id
      }
    })

    engagement.current.impressionId =
      data?.topic?.lastEnrolment?.createMediaImpression?.id ?? null
  }

  const [isHovering, hoverProps] = useHover({
    // TODO it would be nicer to use CSS hover selector, but original implementor did not think of that
    mouseEnterDelayMS: 0
  })
  const mediaElem = useRef<{ wrapper: HTMLDivElement } | null>(null)

  if (isChrome) {
    useLayoutEffect(() => {
      // a NASTY hack to work around the layout computation bug in chrome. Without this, the audio element is squished into just a few pixels because of the animations we have
      setTimeout(() => {
        if (mediaElem && mediaElem.current) {
          const videoElement = mediaElem.current?.wrapper
            ?.children[0] as HTMLVideoElement
          videoElement.style.display = 'none'
          videoElement.style.display = 'block'
        }
      }, 90) // lower values don't mitigate the issue
    })
  }
  useEffect(() => {
    if (!isAdmin) {
      const videoElement = mediaElem.current?.wrapper?.children[0]
      if (videoElement) {
        videoElement.addEventListener(
          'contextmenu',
          (e) => {
            e.preventDefault() // disable context menu on video so that only admins can download videos by clicking into the context menu
            return false
          },
          false
        )
      }
    }
  }, [])

  useEffect(() => {
    if (props.shouldTrackMedia) {
      // When user scrolls down and see whole media element or just hovers mouse over media,
      // automatically create media impression.
      if ((props.isVisible || isHovering) && featureFlags?.video_analytics) {
        createMediaImpression()
      }
    }
  }, [props.isVisible, isHovering, featureFlags?.video_analytics])

  useEffect(() => {
    // If user leaves page with media still playing, let's store video event about it
    return () => {
      if (engagement.current.isPlaying && engagement.current.watchingEndedAt) {
        flushEngagement(VideoEngagementOrigin.VIDEO_PLAYER_DESTROYED)
      }
    }
  }, [])

  let url: null | undefined | string | Array<{ src: string; type: string }> =
    null
  const { data } = props
  const { content, media } = data || {}
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isPaused, setPaused] = useState(true)
  const isMp3 = typeof content === 'string' && content.endsWith('mp3')

  if (media) {
    const { documents } = props

    if (find(documents, ({ status }) => status === 'Error')) {
      return (
        <div className="no-items">
          <IconBan size="large" />
          <h3>
            <Trans>Failed to convert video</Trans>
          </h3>
        </div>
      )
    }
    url = documents.map(({ cdnUrl, mimeType }: IDocument) => {
      return { src: cdnUrl as string, type: mimeType as string }
    })
  } else if (content) {
    const { content } = props.data || {}

    url = content as string
  }

  if (!url) {
    return (
      <div className="no-items">
        <IconGhost size="large" />
        <h3>
          <Trans>No video was set</Trans>
        </h3>
      </div>
    )
  }

  const isYoutube = Boolean(
    typeof url === 'string' && url.match(/www\.youtube\.com/g)
  )

  const flushEngagement = (origin: VideoEngagementOrigin) => {
    if (
      !props.shouldTrackMedia || // Not tracked in edit page
      !props.topicId ||
      !engagement.current.impressionId // If impressionId was not loaded, backend either failed or decided to not provide this
    ) {
      return
    }

    const watchingEndedAt = engagement.current.watchingEndedAt
    const watchingStartedAt = engagement.current.watchingStartedAt

    engagement.current.shouldFlush = false
    engagement.current.watchingStartedAt = watchingEndedAt

    if (watchingEndedAt <= watchingStartedAt) {
      // These values might equal. For example seeking immediately after AUTO_FLUSH when onProgress was not called yet.
      return
    }

    if (
      engagement.current.watchingEndedAt -
        engagement.current.watchingStartedAt >
      AUTO_FLUSH_TIMEOUT_SEC * 2
    ) {
      // React player is calling event handlers undeterministically without any order (which is kinda ok) but we have to adapt to that.
      // For example while seeking sometimes these events are in this order: onPause -> onProgress -> onSeek. (It would be much better to have onPause -> onSeek -> onProgress)
      // This is causing that onProgress might flush wrong data because onSeek was not called yet and correct data are not prepared.
      // Unfortunately onPause handler does not provide any desired data while seeking through the video.
      // And while seeking, this could create gap of dozens of seconds and this should not be flushed, this condition avoids that.
      return
    }

    apolloClientPrivate.mutate<
      StoreMediaEngagementMutation,
      StoreMediaEngagementMutationVariables
    >({
      mutation: StoreMediaEngagementDocument,
      variables: {
        topicId: props.topicId,
        impressionId: engagement.current.impressionId,
        engagement: {
          from_time_ms: Math.trunc(watchingStartedAt * 1000),
          to_time_ms: Math.trunc(watchingEndedAt * 1000),
          origin
        }
      }
    })
  }
  return (
    <Box
      position="relative"
      {...hoverProps}
      className={classNames('VideoPlayer', {
        youtube: isYoutube,
        mp3: isMp3
      })}
    >
      <div
        className={classNames('VideoPlayer__speedDropdownWrapper', {
          'text-white': !isMp3,
          'on-chrome': isChromeLike
        })}
      >
        {!props.hidePlaybackSpeedDropdown &&
          (((isHovering || isPaused) && media) || isMp3) && (
            <Dropdown
              content={
                <>
                  <span>
                    <Trans>Playback speed</Trans>
                  </span>
                  <Slider
                    min={1}
                    marks={{
                      1: '1x',
                      1.1: '1.1x',
                      1.25: '1.25x',
                      1.5: '1.5x'
                    }}
                    step={0.05}
                    included={true}
                    value={playbackRate}
                    max={1.5}
                    onChange={setPlaybackRate}
                  />
                </>
              }
            >
              <div className="icon-down" />
            </Dropdown>
          )}
      </div>

      <ReactPlayer
        width={'100%'}
        height={'100%'}
        ref={mediaElem as any}
        url={url}
        className="react-player-wrapper"
        playbackRate={playbackRate}
        style={{
          minHeight: 54,
          minWidth: 150
        }}
        controls
        onSeek={(e) => {
          // Can not use isPlaying here as seeking has this sequence of events: Pause, Seek, Play.
          // While seeking, it's always paused by onPause handler.
          const { shouldFlush, watchingEndedAt } = engagement.current
          if (shouldFlush && watchingEndedAt) {
            // if watchingEndedAt is missing, user is probably seeking too fast
            // watchingEndedAt is set every second while watching in onProgress handler
            flushEngagement(VideoEngagementOrigin.SEEK)
          }

          engagement.current.watchingStartedAt = e
          engagement.current.watchingEndedAt = e
          engagement.current.shouldFlush = false
        }}
        onProgress={(e) => {
          engagement.current.watchingEndedAt = e.playedSeconds
          if (engagement.current.isPlaying) {
            if (
              e.playedSeconds - engagement.current.watchingStartedAt >
              AUTO_FLUSH_TIMEOUT_SEC
            ) {
              flushEngagement(VideoEngagementOrigin.AUTO_FLUSH)
            }
          } else if (engagement.current.shouldFlush) {
            const event =
              e.played === 1
                ? VideoEngagementOrigin.VIDEO_FINISHED
                : VideoEngagementOrigin.PAUSE

            flushEngagement(event)
          }
        }}
        onPause={() => {
          engagement.current.shouldFlush = true
          engagement.current.isPlaying = false
          setPaused(true)
        }}
        onPlay={async () => {
          // createMediaImpression call should remain here, do not remove. Media can be started
          // without existing impression (without viewing it in viewport or hovering over with mouse),
          // for example by tabbing into it and playing it using spacebar.
          if (props.shouldTrackMedia && featureFlags?.video_analytics) {
            await createMediaImpression()
          }
          engagement.current.isPlaying = true
          setPaused(false)
        }}
        config={{
          file: {
            attributes: { controlsList: 'nodownload' }
          },
          vimeo: {
            playerOptions: {
              controls: 1
            }
          },
          youtube: {
            playerVars: {
              controls: 1,
              modestbranding: 1,
              showinfo: 0,
              autoplay: 0,
              rel: 0,
              fs: 1
            }
          }
        }}
      />
    </Box>
  )
}
